/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
/* biome-ignore-all lint/suspicious/noArrayIndexKey: words and phonemes are ordered sequences */
import { useState, useRef, useEffect } from "react"
import {
  Drawer,
  Descriptions,
  Tag,
  Button,
  Typography,
  Space,
  Card,
  Slider,
  Row,
  Col,
  Progress,
  Alert,
  Tooltip,
  message,
} from "antd"
import {
  CaretRightOutlined,
  PauseOutlined,
  RedoOutlined,
  DownloadOutlined,
  CopyOutlined,
  SoundOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SoundFilled,
} from "@ant-design/icons"
import { DeviceBadge } from "@/components/DeviceBadge"
import type React from "react"
import type {
  SpeechAssessmentLogItem,
  SpeechAssessmentData,
  SpeechAssessmentWord,
  SpeechAssessmentPhoneme,
} from "@/services/log/speechAssessmentApi"
import { UserCellLink } from "@/components/UserCellLink"
import { CDN_URL } from "@/configs/config"
import JsonView from "@uiw/react-json-view"
import { lightTheme } from "@uiw/react-json-view/light"
import dayjs from "dayjs"

const { Text } = Typography

interface SpeechAssessmentDetailDrawerProps {
  open: boolean
  record: SpeechAssessmentLogItem | null
  onClose: () => void
}

export const SpeechAssessmentDetailDrawer: React.FC<SpeechAssessmentDetailDrawerProps> = ({
  open,
  record,
  onClose,
}) => {
  // Audio player states
  const [isPlaying, setIsPlaying] = useState<boolean>(false)
  const [currentTime, setCurrentTime] = useState<number>(0)
  const [duration, setDuration] = useState<number>(0)
  const [volume, setVolume] = useState<number>(1)
  const [isMuted, setIsMuted] = useState<boolean>(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  // Resolve audio URL
  const resolveAudioUrl = (item: SpeechAssessmentLogItem | null): string | null => {
    if (!item) return null
    if (item.audio_url) return item.audio_url
    if (item.audio_path) {
      if (item.audio_path.startsWith("http://") || item.audio_path.startsWith("https://")) {
        return item.audio_path
      }
      const cleanCdn = CDN_URL.replace(/\/+$/, "")
      const cleanPath = item.audio_path.replace(/^\/+/, "")
      return `${cleanCdn}/${cleanPath}`
    }
    return null
  }

  const audioUrl = resolveAudioUrl(record)

  // Reset audio state when record changes or drawer closes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(record?.audio_duration ? Number(record.audio_duration) : 0)
  }, [record, open])

  // Setup HTML5 Audio listeners
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime)
    }

    const handleLoadedMetadata = () => {
      if (audio.duration && !Number.isNaN(audio.duration)) {
        setDuration(audio.duration)
      }
    }

    const handleEnded = () => {
      setIsPlaying(false)
      setCurrentTime(0)
    }

    audio.addEventListener("timeupdate", handleTimeUpdate)
    audio.addEventListener("loadedmetadata", handleLoadedMetadata)
    audio.addEventListener("ended", handleEnded)

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate)
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata)
      audio.removeEventListener("ended", handleEnded)
    }
  }, [audioUrl])

  if (!record) return null

  // Safely parse assessment_data
  let assessmentData: SpeechAssessmentData | null = null
  if (record.assessment_data) {
    if (typeof record.assessment_data === "object") {
      assessmentData = record.assessment_data
    } else {
      try {
        assessmentData = JSON.parse(record.assessment_data)
      } catch {
        assessmentData = null
      }
    }
  }

  const nBest = assessmentData?.NBest?.[0]
  const scores = nBest?.PronunciationAssessment
  const words: SpeechAssessmentWord[] = nBest?.Words || []

  const accuracyScore = scores?.AccuracyScore ?? null
  const fluencyScore = scores?.FluencyScore ?? null
  const completenessScore = scores?.CompletenessScore ?? null
  const pronScore = scores?.PronScore ?? (record.pron_score ? Number(record.pron_score) : null)
  const prosodyScore = scores?.ProsodyScore ?? null

  const handleTogglePlay = () => {
    if (!audioRef.current) return
    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      audioRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false))
    }
  }

  const handleReplay = () => {
    if (!audioRef.current) return
    audioRef.current.currentTime = 0
    setCurrentTime(0)
    audioRef.current
      .play()
      .then(() => setIsPlaying(true))
      .catch(() => setIsPlaying(false))
  }

  const handleSeek = (value: number) => {
    if (!audioRef.current) return
    audioRef.current.currentTime = value
    setCurrentTime(value)
  }

  const handleVolumeChange = (value: number) => {
    if (!audioRef.current) return
    audioRef.current.volume = value
    setVolume(value)
    setIsMuted(value === 0)
  }

  const handleToggleMute = () => {
    if (!audioRef.current) return
    if (isMuted) {
      audioRef.current.volume = volume > 0 ? volume : 0.5
      setIsMuted(false)
    } else {
      audioRef.current.volume = 0
      setIsMuted(true)
    }
  }

  const handleDownload = () => {
    if (!audioUrl) return
    const filename = record.audio_path
      ? record.audio_path.split("/").pop() || "speech_recording.wav"
      : `speech_${record.id}.wav`
    const link = document.createElement("a")
    link.href = audioUrl
    link.download = filename
    link.target = "_blank"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleCopyJson = () => {
    if (record.assessment_data) {
      navigator.clipboard.writeText(JSON.stringify(assessmentData || record.assessment_data, null, 2))
      message.success("Đã sao chép toàn bộ dữ liệu JSON vào clipboard!")
    }
  }

  const formatSeconds = (sec: number) => {
    const mins = Math.floor(sec / 60)
    const secs = Math.floor(sec % 60)
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
  }

  const getScoreColor = (score?: number | null) => {
    if (score === null || score === undefined) return "#d9d9d9"
    if (score >= 80) return "#52c41a"
    if (score >= 50) return "#faad14"
    return "#f5222d"
  }

  return (
    <Drawer
      title={
        <div className="flex flex-wrap items-center justify-between gap-2 pr-6">
          <div className="flex items-center gap-2">
            <SoundOutlined className="text-blue-600 text-xl" />
            <span className="font-bold text-base text-gray-900">Chi tiết Log Chấm Phát Âm #{record.id}</span>
          </div>
          <Space>
            {record.is_success ? (
              <Tag icon={<CheckCircleOutlined />} color="success">
                Thành công
              </Tag>
            ) : (
              <Tag icon={<CloseCircleOutlined />} color="error">
                Thất bại
              </Tag>
            )}
            <Text type="secondary" className="text-xs">
              {dayjs(record.created_at).format("DD/MM/YYYY HH:mm:ss")}
            </Text>
          </Space>
        </div>
      }
      open={open}
      onClose={onClose}
      width={850}
      destroyOnClose
    >
      <div className="flex flex-col gap-5 pb-6">
        {/* Lỗi cảnh báo nếu có */}
        {!record.is_success && record.error_message && (
          <Alert
            type="error"
            showIcon
            message="Lỗi trong quá trình chấm phát âm"
            description={record.error_message}
            className="rounded-lg"
          />
        )}

        {/* 1. Trình phát âm thanh (Audio Player đầy đủ) */}
        <Card
          size="small"
          className="border border-slate-200 bg-slate-50 shadow-xs"
          title={
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 font-semibold text-gray-800">
                <SoundFilled className="text-blue-500" /> Trình phát ghi âm (Audio Player)
              </span>
              {audioUrl && (
                <Button size="small" icon={<DownloadOutlined />} onClick={handleDownload}>
                  Tải file audio (.wav)
                </Button>
              )}
            </div>
          }
        >
          {audioUrl ? (
            <div className="flex flex-col gap-2 p-1">
              <audio ref={audioRef} src={audioUrl} preload="metadata">
                <track kind="captions" />
              </audio>

              {/* Progress Slider */}
              <div className="px-1">
                <Slider
                  min={0}
                  max={duration > 0 ? duration : 100}
                  step={0.05}
                  value={currentTime}
                  onChange={handleSeek}
                  tooltip={{ formatter: (val) => formatSeconds(val || 0) }}
                />
              </div>

              {/* Player Controls Bar */}
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={isPlaying ? <PauseOutlined /> : <CaretRightOutlined />}
                    onClick={handleTogglePlay}
                  />
                  <Button
                    shape="circle"
                    size="middle"
                    icon={<RedoOutlined />}
                    onClick={handleReplay}
                    title="Phát lại từ đầu"
                  />
                  <span className="pl-2 font-mono text-gray-600 text-xs">
                    {formatSeconds(currentTime)} / {formatSeconds(duration)}
                  </span>
                </div>

                <div className="flex w-36 items-center gap-2">
                  <Button
                    type="text"
                    size="small"
                    icon={<SoundOutlined className={isMuted ? "text-gray-400" : "text-blue-500"} />}
                    onClick={handleToggleMute}
                  />
                  <Slider
                    min={0}
                    max={1}
                    step={0.05}
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="py-4 text-center text-gray-400 text-sm">Không tìm thấy URL file âm thanh</div>
          )}
        </Card>

        {/* 2. Thẻ điểm thành phần (Component Scores) */}
        <div>
          <div className="mb-2 font-bold text-gray-800 text-sm uppercase tracking-wide">Điểm số đánh giá phát âm</div>
          <Row gutter={[12, 12]}>
            <Col xs={12} sm={6}>
              <Card size="small" className="border-blue-100 bg-blue-50/30 text-center shadow-xs">
                <div className="mb-1 font-semibold text-gray-600 text-xs">Điểm tổng (Pron)</div>
                <Progress
                  type="circle"
                  percent={pronScore ?? 0}
                  format={(p) => (p !== null ? `${p?.toFixed(1)}` : "—")}
                  strokeColor={getScoreColor(pronScore)}
                  size={65}
                />
                <div className="mt-1 font-medium text-[11px] text-gray-500">Pronunciation</div>
              </Card>
            </Col>

            <Col xs={12} sm={6}>
              <Card size="small" className="border-green-100 bg-green-50/30 text-center shadow-xs">
                <div className="mb-1 font-semibold text-gray-600 text-xs">Độ chuẩn xác</div>
                <Progress
                  type="circle"
                  percent={accuracyScore ?? 0}
                  format={(p) => (p !== null ? `${p?.toFixed(1)}` : "—")}
                  strokeColor={getScoreColor(accuracyScore)}
                  size={65}
                />
                <div className="mt-1 font-medium text-[11px] text-gray-500">Accuracy</div>
              </Card>
            </Col>

            <Col xs={12} sm={6}>
              <Card size="small" className="border-purple-100 bg-purple-50/30 text-center shadow-xs">
                <div className="mb-1 font-semibold text-gray-600 text-xs">Độ lưu loát</div>
                <Progress
                  type="circle"
                  percent={fluencyScore ?? 0}
                  format={(p) => (p !== null ? `${p?.toFixed(1)}` : "—")}
                  strokeColor={getScoreColor(fluencyScore)}
                  size={65}
                />
                <div className="mt-1 font-medium text-[11px] text-gray-500">Fluency</div>
              </Card>
            </Col>

            <Col xs={12} sm={6}>
              <Card size="small" className="border-amber-100 bg-amber-50/30 text-center shadow-xs">
                <div className="mb-1 font-semibold text-gray-600 text-xs">Độ trọn vẹn</div>
                <Progress
                  type="circle"
                  percent={completenessScore ?? 0}
                  format={(p) => (p !== null ? `${p?.toFixed(1)}` : "—")}
                  strokeColor={getScoreColor(completenessScore)}
                  size={65}
                />
                <div className="mt-1 font-medium text-[11px] text-gray-500">Completeness</div>
              </Card>
            </Col>
          </Row>

          {prosodyScore !== null && (
            <div className="mt-2 text-right text-gray-500 text-xs">
              Điểm ngữ điệu (Prosody Score): <strong className="text-gray-700">{prosodyScore.toFixed(1)}</strong>
            </div>
          )}
        </div>

        {/* 3. Chi tiết phân tích từng từ & âm vị (Words & Phonemes Breakdown) */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="font-bold text-gray-800 text-sm uppercase tracking-wide">
              Phân tích chi tiết từng từ & âm vị (Phonemes)
            </span>
            <span className="text-gray-500 text-xs">
              Tổng số từ: <strong className="text-gray-700">{words.length}</strong>
            </span>
          </div>

          {words.length > 0 ? (
            <div className="flex flex-col gap-2.5">
              {words.map((wordItem: SpeechAssessmentWord, idx: number) => {
                const wordAccuracy = wordItem.PronunciationAssessment?.AccuracyScore
                const errorType = wordItem.PronunciationAssessment?.ErrorType || "None"
                const phonemes: SpeechAssessmentPhoneme[] = wordItem.Phonemes || []

                return (
                  <Card key={`${wordItem.Word}-${idx}`} size="small" className="border border-gray-200 shadow-2xs">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-base text-gray-900">{wordItem.Word}</span>
                          {wordAccuracy !== undefined && (
                            <Tag color={getScoreColor(wordAccuracy)} className="font-semibold text-xs">
                              {wordAccuracy.toFixed(1)}
                            </Tag>
                          )}
                          {errorType !== "None" && (
                            <Tag color="red" className="text-xs">
                              {errorType}
                            </Tag>
                          )}
                        </div>
                      </div>

                      {/* Phonemes list */}
                      {phonemes.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 border-gray-100 border-t pt-1">
                          <span className="mr-1 font-medium text-gray-400 text-xs">Âm vị:</span>
                          {phonemes.map((ph: SpeechAssessmentPhoneme, phIdx: number) => {
                            const phScore = ph.PronunciationAssessment?.AccuracyScore
                            const phError = ph.PronunciationAssessment?.ErrorType

                            return (
                              <Tooltip
                                key={`${ph.Phoneme}-${phIdx}`}
                                title={`Âm /${ph.Phoneme}/: ${phScore !== undefined ? phScore.toFixed(1) : "—"}${phError && phError !== "None" ? ` (${phError})` : ""}`}
                              >
                                <span
                                  className="inline-flex items-center gap-1 rounded border px-2 py-0.5 font-mono font-semibold text-xs"
                                  style={{
                                    backgroundColor:
                                      phScore && phScore >= 80
                                        ? "#f6ffed"
                                        : phScore && phScore >= 50
                                          ? "#fffbe6"
                                          : "#fff1f0",
                                    borderColor:
                                      phScore && phScore >= 80
                                        ? "#b7eb8f"
                                        : phScore && phScore >= 50
                                          ? "#ffe58f"
                                          : "#ffa39e",
                                    color:
                                      phScore && phScore >= 80
                                        ? "#389e0d"
                                        : phScore && phScore >= 50
                                          ? "#d48806"
                                          : "#cf1322",
                                  }}
                                >
                                  /{ph.Phoneme}/
                                  <span className="text-[10px] opacity-80">
                                    {phScore !== undefined ? phScore.toFixed(0) : "—"}
                                  </span>
                                </span>
                              </Tooltip>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </Card>
                )
              })}
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-gray-400 text-xs">
              Chưa có dữ liệu phân tích âm vị cho bản ghi này
            </div>
          )}
        </div>

        {/* 4. Thông tin kỹ thuật & Người dùng */}
        <div>
          <div className="mb-2 font-bold text-gray-800 text-sm uppercase tracking-wide">
            Thông tin kỹ thuật & Thiết bị
          </div>
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
            <Descriptions.Item label="Từ / câu mẫu" span={2}>
              <span className="font-bold text-base text-gray-900">{record.reference_text || "—"}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Người dùng">
              <UserCellLink
                user={record.user}
                userId={record.user_id ?? undefined}
                fallback={<Tag color="default">Khách vãng lai</Tag>}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Độ trễ API">
              <span
                className={`font-mono font-semibold ${
                  (record.latency_ms || 0) >= 3000
                    ? "text-red-600"
                    : (record.latency_ms || 0) >= 1500
                      ? "text-amber-600"
                      : "text-green-600"
                }`}
              >
                {record.latency_ms ? `${record.latency_ms} ms` : "—"}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Thời lượng âm thanh">
              <span className="font-mono">
                {record.audio_duration ? `${Number(record.audio_duration).toFixed(2)} giây` : "—"}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label="Thiết bị / Nền tảng">
              <DeviceBadge userAgent={record.user_agent} showDetails />
            </Descriptions.Item>
            <Descriptions.Item label="Địa chỉ IP">
              <span className="font-mono text-gray-700">{record.ip_address || "—"}</span>
            </Descriptions.Item>
            <Descriptions.Item label="User Agent" span={2}>
              <span className="break-all font-mono text-gray-600 text-xs">{record.user_agent || "—"}</span>
            </Descriptions.Item>
          </Descriptions>
        </div>

        {/* 5. JSON Viewer (Dữ liệu Azure raw) */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <span className="font-bold text-gray-800 text-sm uppercase tracking-wide">
              Payload gốc Azure (assessment_data)
            </span>
            {record.assessment_data && (
              <Button size="small" icon={<CopyOutlined />} onClick={handleCopyJson}>
                Sao chép JSON
              </Button>
            )}
          </div>
          {record.assessment_data ? (
            <div className="max-h-96 overflow-auto rounded-lg border border-gray-200 bg-white p-3 font-mono text-xs">
              <JsonView value={assessmentData || (record.assessment_data as any)} collapsed={2} style={lightTheme} />
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-gray-400 text-xs">
              Không có payload JSON
            </div>
          )}
        </div>
      </div>
    </Drawer>
  )
}

export default SpeechAssessmentDetailDrawer
